import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '../components/LanguageSwitcher';

export type SplitHeroShellProps = {
    children: ReactNode;
    contentMaxWidth: number;
    contentSpacing?: number;
};

export function SplitHeroShell({ children, contentMaxWidth, contentSpacing = 12 }: SplitHeroShellProps): ReactElement {
    const { t } = useTranslation();
    const [heroLoaded, setHeroLoaded] = useState(false);
    const markHeroLoaded = useCallback(() => setHeroLoaded(true), []);
    const heroRef = useCallback((image: HTMLImageElement | null) => {
        if (image?.complete) {
            setHeroLoaded(true);
        }
    }, []);
    const heroFade = { opacity: heroLoaded ? 1 : 0, transition: 'opacity 200ms ease-in' };

    return (
        <Stack direction="row" sx={{ minHeight: '100dvh' }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                    height: '100dvh',
                    display: { xs: 'none', md: 'block' },
                    width: { md: 480 },
                    flexShrink: 0,
                    p: 5,
                    overflow: 'hidden',
                    background: (theme) =>
                        `linear-gradient(160deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                }}>
                <Box
                    component="img"
                    ref={heroRef}
                    src="/login-hero.jpg"
                    alt=""
                    aria-hidden
                    onLoad={markHeroLoaded}
                    onError={markHeroLoaded}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        ...heroFade,
                    }}
                />
                {/* Scrim keeps the tagline legible over the photo */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 50%)',
                        ...heroFade,
                    }}
                />
                <Typography
                    variant="h4"
                    component="p"
                    sx={{ position: 'relative', color: 'common.white', maxWidth: 384, ...heroFade }}>
                    {t('AUTH.LOGIN.TAGLINE')}
                </Typography>
            </Box>

            {/* The gutter belongs to the column, not to the content: `contentMaxWidth` is the width the
                designs draw, so padding must not be subtracted from it. */}
            <Stack
                sx={{
                    flex: 1,
                    bgcolor: 'background.paper',
                    alignItems: 'center',
                    overflowY: 'auto',
                    px: { xs: 2, sm: 3 },
                }}>
                <Stack sx={{ width: '100%', maxWidth: contentMaxWidth, py: 6, my: 'auto' }} spacing={contentSpacing}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box
                            component="img"
                            src="/houzy_logo.svg"
                            alt={t('AUTH.LOGIN.LOGO_ALT')}
                            sx={{ height: 32, width: 109, objectFit: 'contain' }}
                        />
                        <LanguageSwitcher />
                    </Stack>

                    {children}
                </Stack>
            </Stack>
        </Stack>
    );
}
